/**
 * CREATIVE DETONATOR - Presentation Mode v2.0
 * Full-screen immersive idea presentation with 3 view modes, format navigation, 8-bit sounds
 */

// 8-BIT SOUNDS
class PixelSounds {
  constructor() { this.ctx = null; this.enabled = true; this.vol = 0.25; }
  init() { if (this.ctx) return; try { this.ctx = new (window.AudioContext || window.webkitAudioContext)(); } catch(e) { this.enabled = false; } }
  osc(type, freq, dur) {
    if (!this.enabled || !this.ctx) return;
    const o = this.ctx.createOscillator(), g = this.ctx.createGain();
    o.type = type; o.frequency.value = freq; o.connect(g); g.connect(this.ctx.destination);
    const t = this.ctx.currentTime;
    g.gain.setValueAtTime(this.vol, t); g.gain.exponentialRampToValueAtTime(0.01, t + dur);
    o.start(t); o.stop(t + dur);
  }
  detonate() { if (!this.enabled) return; this.init(); this.osc('square', 150, 0.15); setTimeout(() => this.osc('square', 100, 0.1), 50); }
  click() { if (!this.enabled) return; this.init(); this.osc('square', 800, 0.03); }
  save() { if (!this.enabled) return; this.init(); [523, 659, 784].forEach((f, i) => setTimeout(() => this.osc('square', f, 0.1), i * 60)); }
  unsave() { if (!this.enabled) return; this.init(); this.osc('square', 300, 0.1); }
  countdown(final) { if (!this.enabled) return; this.init(); this.osc('square', final ? 880 : 440, final ? 0.2 : 0.1); }
  copy() { if (!this.enabled) return; this.init(); this.osc('square', 1000, 0.05); }
  exit() { if (!this.enabled) return; this.init(); this.osc('sawtooth', 200, 0.15); }
  modeSwitch() { if (!this.enabled) return; this.init(); this.osc('square', 600, 0.05); setTimeout(() => this.osc('square', 800, 0.05), 50); }
  formatSwitch() { if (!this.enabled) return; this.init(); this.osc('triangle', 400, 0.08); }
  toggle() { this.enabled = !this.enabled; return this.enabled; }
}

// PARTICLES
class Particles {
  constructor(el) { this.el = el; this.ps = []; }
  emit(x, y) {
    for (let i = 0; i < 15; i++) {
      const p = document.createElement('div'); p.className = 'pres-particle';
      const sz = 2 + Math.random() * 4, a = (Math.PI * 2 * i) / 15, v = 4 + Math.random() * 6;
      p.style.cssText = `left:${x}px;top:${y}px;width:${sz}px;height:${sz}px;`;
      this.el.appendChild(p);
      this.ps.push({ el: p, x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, life: 1 });
    }
    this.run();
  }
  run() {
    const tick = () => {
      this.ps = this.ps.filter(p => {
        p.x += p.vx; p.y += p.vy; p.vy += 0.3; p.life -= 0.03;
        if (p.life <= 0) { p.el.remove(); return false; }
        p.el.style.left = p.x + 'px'; p.el.style.top = p.y + 'px'; p.el.style.opacity = p.life;
        return true;
      });
      if (this.ps.length) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }
}

// DUMMY IMAGES for visual formats when no real image
const PRES_DUMMY_IMAGES = [
  '/assets/images/dummy/print1.png',
  '/assets/images/dummy/print2.png',
  '/assets/images/dummy/print3.png',
  '/assets/images/dummy/print4.png'
];
let presDummyIndex = 0;

// Visual formats that support images
const VISUAL_FORMATS = ['print', 'social', 'digital', 'experiential'];

// FORMAT ICONS - Now uses centralized icons.js (Single Source of Truth)
// Access via window.Icons.formatOutline(format) from shared/icons.js
function getPresFormatIcon(format) {
  // Use centralized icon system if available, fallback to inline SVG
  if (window.Icons?.formatOutline) {
    return window.Icons.formatOutline(format);
  }
  // Fallback for legacy compatibility
  const FALLBACK_ICONS = {
    print: `<svg viewBox="0 0 32 32" fill="currentColor"><rect x="4" y="4" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2"/><rect x="8" y="8" width="16" height="4"/><rect x="8" y="14" width="12" height="2"/><rect x="8" y="18" width="14" height="2"/></svg>`,
    video: `<svg viewBox="0 0 32 32" fill="currentColor"><rect x="2" y="6" width="28" height="20" fill="none" stroke="currentColor" stroke-width="2"/><polygon points="12,10 12,22 22,16"/></svg>`,
    social: `<svg viewBox="0 0 32 32" fill="currentColor"><rect x="8" y="2" width="16" height="28" rx="2" fill="none" stroke="currentColor" stroke-width="2"/><rect x="14" y="26" width="4" height="2"/></svg>`,
    experiential: `<svg viewBox="0 0 32 32" fill="currentColor"><circle cx="16" cy="16" r="12" fill="none" stroke="currentColor" stroke-width="2"/><circle cx="16" cy="16" r="4"/></svg>`,
    digital: `<svg viewBox="0 0 32 32" fill="currentColor"><rect x="2" y="4" width="28" height="20" fill="none" stroke="currentColor" stroke-width="2"/><rect x="10" y="24" width="12" height="4" fill="none" stroke="currentColor" stroke-width="2"/></svg>`,
    campaign: `<svg viewBox="0 0 32 32" fill="currentColor"><rect x="4" y="8" width="24" height="16" fill="none" stroke="currentColor" stroke-width="2"/><polygon points="16,4 20,8 12,8"/></svg>`
  };
  return FALLBACK_ICONS[format] || FALLBACK_ICONS.print;
}

// VIEW MODES
const VIEW_MODES = {
  FOCUS: 'focus',
  LIST: 'list',
  GALLERY: 'gallery'
};

// FORMAT NAMES
const FORMAT_NAMES = {
  print: 'PRINT / OOH',
  video: 'VIDEO',
  social: 'SOCIAL',
  experiential: 'EXPERIENTIAL',
  digital: 'DIGITAL',
  campaign: 'CAMPAIGN'
};

// PRESENTATION MODE
class PresentationMode {
  constructor(opts = {}) {
    this.ideas = [];
    this.idx = 0;
    this.saved = new Set();
    this.sounds = new PixelSounds();
    this.particles = null;
    this.overlay = null;
    this.onClose = opts.onClose || null;
    this.onSave = opts.onSave || null;
    this.onRefine = opts.onRefine || null;
    this.onGenerateImage = opts.onGenerateImage || null;
    this.keyHandler = this.onKey.bind(this);

    // View mode state
    this.viewMode = VIEW_MODES.FOCUS;

    // Format navigation state
    this.formats = [];
    this.formatIndex = 0;
    this.ideasByFormat = {};
    this.ideaIndexInFormat = 0;
  }

  open(ideas, start = 0) {
    if (!ideas?.length) return;
    this.ideas = ideas;
    this.idx = start;
    this.sounds.init();

    // Organize ideas by format
    this.organizeByFormat();

    this.createOverlay();
    window.Events.onDocument('keydown', this.keyHandler, 'presentation');
    document.body.style.overflow = 'hidden';
    this.playIntro();
  }

  organizeByFormat() {
    this.ideasByFormat = {};
    this.ideas.forEach((idea, globalIdx) => {
      const fmt = (idea.format || 'print').toLowerCase();
      if (!this.ideasByFormat[fmt]) {
        this.ideasByFormat[fmt] = [];
      }
      this.ideasByFormat[fmt].push({ idea, globalIdx });
    });
    this.formats = Object.keys(this.ideasByFormat);

    // Set initial format based on first idea
    const firstFormat = this.ideas[this.idx]?.format || 'print';
    this.formatIndex = this.formats.indexOf(firstFormat);
    if (this.formatIndex < 0) this.formatIndex = 0;

    // Find idea index within format
    this.updateIdeaIndexInFormat();
  }

  updateIdeaIndexInFormat() {
    const currentFormat = this.formats[this.formatIndex];
    const formatIdeas = this.ideasByFormat[currentFormat] || [];
    const found = formatIdeas.findIndex(item => item.globalIdx === this.idx);
    this.ideaIndexInFormat = found >= 0 ? found : 0;
  }

  close() {
    this.sounds.exit();
    window.Events.cleanupScope('presentation');
    this.showSummary();
  }

  destroy() {
    // Cleanup scoped events
    window.Events.cleanupScope('presentation');
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }
    document.body.style.overflow = '';
    if (this.onClose) this.onClose();
  }

  createOverlay() {
    this.overlay = document.createElement('div');
    this.overlay.className = 'pres-overlay';
    this.overlay.innerHTML = `
      <!-- INTRO SCREEN -->
      <div class="pres-intro">
        <div class="pres-intro-text">LOADING...</div>
        <div class="pres-intro-progress"><div class="pres-intro-bar"></div></div>
        <div class="pres-countdown"></div>
      </div>

      <!-- UNIFIED HEADER -->
      <header class="pres-header" style="display:none;">
        <div class="pres-header-left">
          <button class="pres-close" title="Exit (ESC)">
            <span class="pres-key-cap">ESC</span>
          </button>
          <span class="pres-logo">CREATIVE DETONATOR</span>
        </div>
        <div class="pres-header-center">
          <!-- Format Tabs (integrated) -->
          <div class="pres-format-tabs"></div>
        </div>
        <div class="pres-header-right">
          <!-- View Mode Dropdown -->
          <div class="pres-view-dropdown">
            <button class="pres-view-dropdown-btn" title="View Mode (1/2/3)">
              <span class="pres-view-dropdown-icon">◉</span>
              <span class="pres-view-dropdown-label">FOCUS</span>
              <span class="pres-view-dropdown-arrow">▼</span>
            </button>
            <div class="pres-view-dropdown-menu hidden">
              <button class="pres-view-option active" data-view="focus">
                <span class="pres-view-option-icon">◉</span>
                <span class="pres-view-option-label">FOCUS</span>
                <span class="pres-view-option-key">1</span>
              </button>
              <button class="pres-view-option" data-view="list">
                <span class="pres-view-option-icon">☰</span>
                <span class="pres-view-option-label">LIST</span>
                <span class="pres-view-option-key">2</span>
              </button>
              <button class="pres-view-option" data-view="gallery">
                <span class="pres-view-option-icon">▦</span>
                <span class="pres-view-option-label">GALLERY</span>
                <span class="pres-view-option-key">3</span>
              </button>
            </div>
          </div>
          <div class="pres-counter">
            <span class="pres-cur">1</span>/<span class="pres-tot">1</span>
          </div>
        </div>
      </header>

      <!-- MAIN CONTENT AREA -->
      <main class="pres-content" style="display:none;">
        <div class="pres-particles"></div>

        <!-- FOCUS VIEW (single idea) -->
        <div class="pres-view pres-view-focus active">
          <div class="pres-idea"></div>
        </div>

        <!-- LIST VIEW -->
        <div class="pres-view pres-view-list">
          <div class="pres-list-container"></div>
        </div>

        <!-- GALLERY VIEW -->
        <div class="pres-view pres-view-gallery">
          <div class="pres-gallery-container"></div>
        </div>
      </main>

      <!-- PROGRESS BAR -->
      <div class="pres-progress"><div class="pres-progress-bar"></div></div>

      <!-- CONTROL BAR (Floating Footer) -->
      <footer class="pres-footer" style="display:none;">
        <div class="pres-control-group pres-controls-nav">
          <div class="pres-control-label">NAVIGATE</div>
          <div class="pres-control-keys">
            <button class="pres-key-btn pres-prev" title="Previous idea">
              <span class="pres-key-cap">←</span>
            </button>
            <button class="pres-key-btn pres-next" title="Next idea">
              <span class="pres-key-cap">→</span>
            </button>
          </div>
        </div>

        <div class="pres-control-divider"></div>

        <div class="pres-control-group pres-controls-format">
          <div class="pres-control-label">FORMAT</div>
          <div class="pres-control-keys">
            <button class="pres-key-btn pres-format-up" title="Previous format">
              <span class="pres-key-cap">↑</span>
            </button>
            <button class="pres-key-btn pres-format-down" title="Next format">
              <span class="pres-key-cap">↓</span>
            </button>
          </div>
        </div>

        <div class="pres-control-divider"></div>

        <div class="pres-control-group pres-controls-actions">
          <div class="pres-control-label">ACTIONS</div>
          <div class="pres-control-keys">
            <button class="pres-key-btn pres-action-save" title="Save idea (S)">
              <span class="pres-key-cap">S</span>
              <span class="pres-key-label">SAVE</span>
              <span class="pres-save-ind">
                <svg class="pres-star-icon" viewBox="0 0 12 12" width="12" height="12">
                  <!-- 8-bit pixel star using rectangles -->
                  <g class="pres-star-pixels">
                    <!-- Top point -->
                    <rect x="5" y="0" width="2" height="2"/>
                    <!-- Upper arms -->
                    <rect x="4" y="2" width="4" height="2"/>
                    <!-- Wide middle -->
                    <rect x="0" y="4" width="12" height="2"/>
                    <!-- Lower body -->
                    <rect x="2" y="6" width="8" height="2"/>
                    <!-- Bottom legs -->
                    <rect x="2" y="8" width="2" height="2"/>
                    <rect x="8" y="8" width="2" height="2"/>
                    <!-- Feet -->
                    <rect x="0" y="10" width="2" height="2"/>
                    <rect x="10" y="10" width="2" height="2"/>
                  </g>
                </svg>
              </span>
            </button>
            <button class="pres-key-btn pres-action-copy" title="Copy to clipboard (C)">
              <span class="pres-key-cap">C</span>
              <span class="pres-key-label">COPY</span>
            </button>
          </div>
        </div>

        <div class="pres-control-divider"></div>

        <div class="pres-control-group pres-controls-system">
          <div class="pres-control-label">SYSTEM</div>
          <div class="pres-control-keys">
            <button class="pres-key-btn pres-action-sound" title="Toggle sound (M)">
              <span class="pres-key-cap">M</span>
              <span class="pres-key-label pres-sound-label">ON</span>
            </button>
            <button class="pres-key-btn pres-action-help" title="Show help (?)">
              <span class="pres-key-cap">?</span>
              <span class="pres-key-label">HELP</span>
            </button>
          </div>
        </div>
      </footer>

      <!-- HELP OVERLAY -->
      <div class="pres-help hidden">
        <div class="pres-help-card">
          <div class="pres-help-close-btn">×</div>
          <div class="pres-help-title">KEYBOARD CONTROLS</div>
          <div class="pres-help-sections">
            <div class="pres-help-section">
              <div class="pres-help-section-title">NAVIGATION</div>
              <div class="pres-help-row">
                <div class="pres-help-keys"><span class="pres-help-key">←</span><span class="pres-help-key">→</span></div>
                <div class="pres-help-desc">Previous / Next idea</div>
              </div>
              <div class="pres-help-row">
                <div class="pres-help-keys"><span class="pres-help-key">↑</span><span class="pres-help-key">↓</span></div>
                <div class="pres-help-desc">Switch format</div>
              </div>
              <div class="pres-help-row">
                <div class="pres-help-keys"><span class="pres-help-key">SPACE</span></div>
                <div class="pres-help-desc">Next idea</div>
              </div>
            </div>
            <div class="pres-help-section">
              <div class="pres-help-section-title">VIEW MODES</div>
              <div class="pres-help-row">
                <div class="pres-help-keys"><span class="pres-help-key">1</span></div>
                <div class="pres-help-desc">Focus view (single idea)</div>
              </div>
              <div class="pres-help-row">
                <div class="pres-help-keys"><span class="pres-help-key">2</span></div>
                <div class="pres-help-desc">List view (all ideas)</div>
              </div>
              <div class="pres-help-row">
                <div class="pres-help-keys"><span class="pres-help-key">3</span></div>
                <div class="pres-help-desc">Gallery view (visuals)</div>
              </div>
            </div>
            <div class="pres-help-section">
              <div class="pres-help-section-title">ACTIONS</div>
              <div class="pres-help-row">
                <div class="pres-help-keys"><span class="pres-help-key">S</span></div>
                <div class="pres-help-desc">Save / Unsave idea</div>
              </div>
              <div class="pres-help-row">
                <div class="pres-help-keys"><span class="pres-help-key">C</span></div>
                <div class="pres-help-desc">Copy idea to clipboard</div>
              </div>
              <div class="pres-help-row">
                <div class="pres-help-keys"><span class="pres-help-key">M</span></div>
                <div class="pres-help-desc">Toggle sound</div>
              </div>
              <div class="pres-help-row">
                <div class="pres-help-keys"><span class="pres-help-key">ESC</span></div>
                <div class="pres-help-desc">Exit presentation</div>
              </div>
            </div>
          </div>
          <div class="pres-help-footer">Click anywhere or press any key to close</div>
        </div>
      </div>

      <!-- SUMMARY SCREEN -->
      <div class="pres-summary hidden">
        <div class="pres-summary-card">
          <div class="pres-summary-title">SESSION COMPLETE</div>
          <div class="pres-summary-stats">
            <div class="pres-summary-stat">
              <span class="pres-summary-label">Ideas Viewed</span>
              <span class="pres-summary-value pres-viewed">0</span>
            </div>
            <div class="pres-summary-stat">
              <span class="pres-summary-label">Ideas Saved</span>
              <span class="pres-summary-value">
                <span class="pres-savedct">0</span>
                <span class="pres-summary-stars"></span>
              </span>
            </div>
          </div>
          <button class="pres-summary-btn">DONE</button>
        </div>
      </div>

      <!-- NOTIFICATION -->
      <div class="pres-notification"></div>
    `;

    document.body.appendChild(this.overlay);
    this.particles = new Particles(this.overlay.querySelector('.pres-particles'));

    // Bind event handlers
    this.bindEvents();

    // Build format tabs
    this.buildFormatTabs();
  }

  bindEvents() {
    // Header buttons
    this.overlay.querySelector('.pres-close').onclick = () => { this.sounds.click(); this.close(); };

    // View mode dropdown
    const dropdownBtn = this.overlay.querySelector('.pres-view-dropdown-btn');
    const dropdownMenu = this.overlay.querySelector('.pres-view-dropdown-menu');

    dropdownBtn.onclick = (e) => {
      e.stopPropagation();
      this.sounds.click();
      dropdownMenu.classList.toggle('hidden');
    };

    // View mode options
    this.overlay.querySelectorAll('.pres-view-option').forEach(opt => {
      opt.onclick = (e) => {
        e.stopPropagation();
        this.sounds.click();
        dropdownMenu.classList.add('hidden');
        this.setViewMode(opt.dataset.view);
      };
    });

    // Close dropdown when clicking outside
    this.overlay.addEventListener('click', () => {
      dropdownMenu.classList.add('hidden');
    });

    // Navigation keys (in footer control bar)
    this.overlay.querySelector('.pres-prev').onclick = () => { this.sounds.click(); this.prev(); };
    this.overlay.querySelector('.pres-next').onclick = () => { this.sounds.click(); this.next(); };
    this.overlay.querySelector('.pres-format-up').onclick = () => { this.sounds.click(); this.prevFormat(); };
    this.overlay.querySelector('.pres-format-down').onclick = () => { this.sounds.click(); this.nextFormat(); };

    // Action keys
    this.overlay.querySelector('.pres-action-save').onclick = () => { this.toggleSave(); };
    this.overlay.querySelector('.pres-action-copy').onclick = () => { this.copy(); };
    this.overlay.querySelector('.pres-action-sound').onclick = () => { this.toggleSound(); };
    this.overlay.querySelector('.pres-action-help').onclick = () => { this.sounds.click(); this.showHelp(); };

    // Help overlay close
    const helpOverlay = this.overlay.querySelector('.pres-help');
    helpOverlay.onclick = (e) => {
      if (e.target === helpOverlay || e.target.classList.contains('pres-help-close-btn')) {
        this.hideHelp();
      }
    };

    // Summary done button
    this.overlay.querySelector('.pres-summary-btn').onclick = () => { this.sounds.click(); this.destroy(); };
  }

  buildFormatTabs() {
    const container = this.overlay.querySelector('.pres-format-tabs');
    container.innerHTML = '';

    this.formats.forEach((fmt, idx) => {
      const count = this.ideasByFormat[fmt].length;
      const tab = document.createElement('button');
      tab.className = 'pres-format-tab' + (idx === this.formatIndex ? ' active' : '');
      tab.dataset.format = fmt;
      tab.innerHTML = `
        <span class="pres-format-tab-icon">${getPresFormatIcon(fmt)}</span>
        <span class="pres-format-tab-name">${fmt.toUpperCase()}</span>
        <span class="pres-format-tab-count">${count}</span>
      `;
      tab.onclick = () => {
        this.sounds.click();
        this.setFormat(idx);
      };
      container.appendChild(tab);
    });
  }

  onKey(e) {
    // Help overlay open - any key closes it
    const help = this.overlay.querySelector('.pres-help');
    if (!help.classList.contains('hidden')) {
      this.hideHelp();
      return;
    }

    // Summary screen
    const sum = this.overlay.querySelector('.pres-summary');
    if (!sum.classList.contains('hidden')) {
      if (e.key === 'Escape' || e.key === 'Enter') this.destroy();
      return;
    }

    // Intro screen
    const intro = this.overlay.querySelector('.pres-intro');
    if (!intro.classList.contains('hidden')) {
      if (e.key === 'Escape') this.skipIntro();
      return;
    }

    // Main navigation
    switch (e.key) {
      case 'ArrowRight':
      case ' ':
        e.preventDefault();
        this.sounds.click();
        this.next();
        break;
      case 'ArrowLeft':
        e.preventDefault();
        this.sounds.click();
        this.prev();
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.sounds.click();
        this.prevFormat();
        break;
      case 'ArrowDown':
        e.preventDefault();
        this.sounds.click();
        this.nextFormat();
        break;
      case 'Escape':
        this.close();
        break;
      case 's':
      case 'S':
        this.toggleSave();
        break;
      case 'c':
      case 'C':
        this.copy();
        break;
      case 'm':
      case 'M':
        this.toggleSound();
        break;
      case '?':
        this.showHelp();
        break;
      case '1':
        this.sounds.click();
        this.setViewMode(VIEW_MODES.FOCUS);
        break;
      case '2':
        this.sounds.click();
        this.setViewMode(VIEW_MODES.LIST);
        break;
      case '3':
        this.sounds.click();
        this.setViewMode(VIEW_MODES.GALLERY);
        break;
    }
  }

  async playIntro() {
    const bar = this.overlay.querySelector('.pres-intro-bar');
    const txt = this.overlay.querySelector('.pres-intro-text');
    const cd = this.overlay.querySelector('.pres-countdown');

    bar.style.transition = 'width 600ms steps(15)';
    bar.style.width = '100%';
    await this.wait(700);

    txt.textContent = 'READY';
    await this.wait(200);

    this.overlay.querySelector('.pres-intro-progress').style.display = 'none';
    txt.style.display = 'none';

    for (let i = 3; i >= 1; i--) {
      cd.textContent = i;
      cd.classList.remove('visible');
      void cd.offsetWidth;
      cd.classList.add('visible');
      this.sounds.countdown(false);
      await this.wait(350);
    }

    cd.textContent = 'GO!';
    cd.classList.remove('visible');
    void cd.offsetWidth;
    cd.classList.add('visible');
    this.sounds.countdown(true);
    await this.wait(400);

    this.skipIntro();
  }

  skipIntro() {
    this.overlay.querySelector('.pres-intro').classList.add('hidden');
    this.overlay.querySelector('.pres-header').style.display = 'flex';
    this.overlay.querySelector('.pres-content').style.display = 'block';
    this.overlay.querySelector('.pres-footer').style.display = 'flex';
    this.show(this.idx, false);
  }

  // ============================================================================
  // VIEW MODE SWITCHING
  // ============================================================================

  setViewMode(mode) {
    if (this.viewMode === mode) return;

    this.viewMode = mode;
    this.sounds.modeSwitch();

    // Update dropdown button to reflect current mode
    const dropdownBtn = this.overlay.querySelector('.pres-view-dropdown-btn');
    const icons = { focus: '◉', list: '☰', gallery: '▦' };
    const labels = { focus: 'FOCUS', list: 'LIST', gallery: 'GALLERY' };
    dropdownBtn.querySelector('.pres-view-dropdown-icon').textContent = icons[mode];
    dropdownBtn.querySelector('.pres-view-dropdown-label').textContent = labels[mode];

    // Update dropdown menu active states
    this.overlay.querySelectorAll('.pres-view-option').forEach(opt => {
      opt.classList.toggle('active', opt.dataset.view === mode);
    });

    // Show/hide views
    this.overlay.querySelectorAll('.pres-view').forEach(view => {
      view.classList.remove('active');
    });
    this.overlay.querySelector(`.pres-view-${mode}`).classList.add('active');

    // Update view-specific UI (show/hide format tabs and navigation controls)
    const formatTabs = this.overlay.querySelector('.pres-format-tabs');
    const controlsNav = this.overlay.querySelector('.pres-controls-nav');
    const controlsFormat = this.overlay.querySelector('.pres-controls-format');

    if (mode === VIEW_MODES.FOCUS) {
      formatTabs.style.display = 'flex';
      controlsNav.style.display = 'flex';
      controlsFormat.style.display = 'flex';
    } else {
      formatTabs.style.display = 'none';
      controlsNav.style.display = 'none';
      controlsFormat.style.display = 'none';
    }

    // Render view content
    if (mode === VIEW_MODES.LIST) {
      this.renderListView();
    } else if (mode === VIEW_MODES.GALLERY) {
      this.renderGalleryView();
    }

    this.notify(mode.toUpperCase() + ' VIEW');
  }

  renderListView() {
    // COPYWRITER VIEW - Headlines only, clean and minimal
    const container = this.overlay.querySelector('.pres-list-container');
    container.innerHTML = '';

    // Add header
    const header = document.createElement('div');
    header.className = 'pres-list-header';
    header.innerHTML = `
      <div class="pres-list-header-title">HEADLINES</div>
      <div class="pres-list-header-count">${this.ideas.length} ideas</div>
    `;
    container.appendChild(header);

    // Create single list of all headlines
    const ideasList = document.createElement('div');
    ideasList.className = 'pres-list-ideas';

    let ideaNum = 1;
    this.ideas.forEach((idea, globalIdx) => {
      // Only show ideas with headlines
      if (!idea.headline) return;

      const ideaEl = document.createElement('div');
      ideaEl.className = 'pres-list-item' + (this.saved.has(idea.id || globalIdx) ? ' saved' : '');
      ideaEl.dataset.index = globalIdx;
      ideaEl.innerHTML = `
        <div class="pres-list-item-num">${ideaNum}</div>
        <div class="pres-list-item-headline">"${this.esc(idea.headline)}"</div>
        <div class="pres-list-item-actions">
          <button class="pres-list-btn pres-list-save-btn ${this.saved.has(idea.id || globalIdx) ? 'saved' : ''}" title="Save">
            ${this.saved.has(idea.id || globalIdx) ? '★' : '☆'}
          </button>
        </div>
      `;
      ideaNum++;

      // Click row to view
      ideaEl.onclick = (e) => {
        if (e.target.classList.contains('pres-list-save-btn')) return;
        this.sounds.click();
        this.idx = globalIdx;
        this.updateIdeaIndexInFormat();
        this.updateFormatTabs();
        this.setViewMode(VIEW_MODES.FOCUS);
        this.show(this.idx, false);
      };

      // Click to save
      ideaEl.querySelector('.pres-list-save-btn').onclick = (e) => {
        e.stopPropagation();
        const id = idea.id || globalIdx;
        if (this.saved.has(id)) {
          this.saved.delete(id);
          this.sounds.unsave();
          e.target.textContent = '☆';
          e.target.classList.remove('saved');
          ideaEl.classList.remove('saved');
        } else {
          this.saved.add(id);
          this.sounds.save();
          e.target.textContent = '★';
          e.target.classList.add('saved');
          ideaEl.classList.add('saved');
        }
        if (this.onSave) this.onSave(idea, this.saved.has(id));
      };

      ideasList.appendChild(ideaEl);
    });

    container.appendChild(ideasList);
  }

  renderGalleryView() {
    // VISUAL GALLERY - Images + headlines only, clean grid
    const container = this.overlay.querySelector('.pres-gallery-container');
    container.innerHTML = '';

    // Collect all visual ideas
    const visualIdeas = [];
    this.ideas.forEach((idea, globalIdx) => {
      const fmt = (idea.format || 'print').toLowerCase();
      if (VISUAL_FORMATS.includes(fmt)) {
        visualIdeas.push({ idea, globalIdx });
      }
    });

    if (visualIdeas.length === 0) {
      container.innerHTML = `
        <div class="pres-gallery-empty">
          <div class="pres-gallery-empty-icon">▦</div>
          <div class="pres-gallery-empty-text">No visuals available</div>
        </div>
      `;
      return;
    }

    // Single grid, no format headers
    const grid = document.createElement('div');
    grid.className = 'pres-gallery-grid';

    visualIdeas.forEach(({ idea, globalIdx }) => {
      let imageUrl = idea.imageUrl;
      if (!imageUrl) {
        imageUrl = PRES_DUMMY_IMAGES[presDummyIndex % PRES_DUMMY_IMAGES.length];
        presDummyIndex++;
      }

      const item = document.createElement('div');
      item.className = 'pres-gallery-item' + (this.saved.has(idea.id || globalIdx) ? ' saved' : '');
      item.dataset.index = globalIdx;
      item.innerHTML = `
        <div class="pres-gallery-image-wrap">
          <img src="${imageUrl}" alt="" class="pres-gallery-image" />
          <div class="pres-gallery-overlay">
            <button class="pres-gallery-btn pres-gallery-view" title="View">◉</button>
            <button class="pres-gallery-btn pres-gallery-save-btn ${this.saved.has(idea.id || globalIdx) ? 'saved' : ''}" title="Save">
              ${this.saved.has(idea.id || globalIdx) ? '★' : '☆'}
            </button>
          </div>
        </div>
        ${idea.headline ? `<div class="pres-gallery-headline">"${this.esc(idea.headline)}"</div>` : ''}
      `;

      // Click image to view
      item.querySelector('.pres-gallery-view').onclick = (e) => {
        e.stopPropagation();
        this.sounds.click();
        this.idx = globalIdx;
        this.updateIdeaIndexInFormat();
        this.updateFormatTabs();
        this.setViewMode(VIEW_MODES.FOCUS);
        this.show(this.idx, false);
      };

      // Save button
      const saveBtn = item.querySelector('.pres-gallery-save-btn');
      if (saveBtn) {
        saveBtn.onclick = (e) => {
          e.stopPropagation();
          const id = idea.id || globalIdx;
          if (this.saved.has(id)) {
            this.saved.delete(id);
            this.sounds.unsave();
            e.target.textContent = '☆';
            e.target.classList.remove('saved');
            item.classList.remove('saved');
          } else {
            this.saved.add(id);
            this.sounds.save();
            e.target.textContent = '★';
            e.target.classList.add('saved');
            item.classList.add('saved');
          }
          if (this.onSave) this.onSave(idea, this.saved.has(id));
        };
      }

      grid.appendChild(item);
    });

    container.appendChild(grid);
  }

  // ============================================================================
  // FORMAT NAVIGATION
  // ============================================================================

  prevFormat() {
    if (this.formats.length <= 1) return;
    this.formatIndex = (this.formatIndex - 1 + this.formats.length) % this.formats.length;
    this.sounds.formatSwitch();
    this.jumpToFormat();
  }

  nextFormat() {
    if (this.formats.length <= 1) return;
    this.formatIndex = (this.formatIndex + 1) % this.formats.length;
    this.sounds.formatSwitch();
    this.jumpToFormat();
  }

  setFormat(idx) {
    if (idx === this.formatIndex) return;
    this.formatIndex = idx;
    this.sounds.formatSwitch();
    this.jumpToFormat();
  }

  jumpToFormat() {
    const fmt = this.formats[this.formatIndex];
    const formatIdeas = this.ideasByFormat[fmt];
    if (formatIdeas && formatIdeas.length > 0) {
      // Jump to first idea in format
      this.ideaIndexInFormat = 0;
      this.idx = formatIdeas[0].globalIdx;
      this.updateFormatTabs();
      this.show(this.idx, true);
    }
  }

  updateFormatTabs() {
    this.overlay.querySelectorAll('.pres-format-tab').forEach((tab, idx) => {
      tab.classList.toggle('active', idx === this.formatIndex);
    });
  }

  // ============================================================================
  // IDEA NAVIGATION
  // ============================================================================

  next() {
    if (this.viewMode !== VIEW_MODES.FOCUS) return;

    const fmt = this.formats[this.formatIndex];
    const formatIdeas = this.ideasByFormat[fmt];

    if (this.ideaIndexInFormat < formatIdeas.length - 1) {
      this.ideaIndexInFormat++;
      this.idx = formatIdeas[this.ideaIndexInFormat].globalIdx;
      this.show(this.idx, true);
    } else if (this.formatIndex < this.formats.length - 1) {
      // Move to next format
      this.nextFormat();
    }
  }

  prev() {
    if (this.viewMode !== VIEW_MODES.FOCUS) return;

    const fmt = this.formats[this.formatIndex];
    const formatIdeas = this.ideasByFormat[fmt];

    if (this.ideaIndexInFormat > 0) {
      this.ideaIndexInFormat--;
      this.idx = formatIdeas[this.ideaIndexInFormat].globalIdx;
      this.show(this.idx, true);
    } else if (this.formatIndex > 0) {
      // Move to previous format
      this.prevFormat();
      // Jump to last idea in that format
      const newFmt = this.formats[this.formatIndex];
      const newFormatIdeas = this.ideasByFormat[newFmt];
      this.ideaIndexInFormat = newFormatIdeas.length - 1;
      this.idx = newFormatIdeas[this.ideaIndexInFormat].globalIdx;
      this.show(this.idx, true);
    }
  }

  go(i) {
    if (i !== this.idx && i >= 0 && i < this.ideas.length) {
      this.idx = i;
      this.updateIdeaIndexInFormat();
      this.show(i, true);
    }
  }

  // ============================================================================
  // DISPLAY IDEA
  // ============================================================================

  async show(i, anim = true) {
    const idea = this.ideas[i];
    const cont = this.overlay.querySelector('.pres-idea');

    // Update counter
    const currentFormat = this.formats[this.formatIndex];
    const formatIdeas = this.ideasByFormat[currentFormat] || [];
    this.overlay.querySelector('.pres-cur').textContent = this.ideaIndexInFormat + 1;
    this.overlay.querySelector('.pres-tot').textContent = formatIdeas.length;

    // Update progress
    this.overlay.querySelector('.pres-progress-bar').style.width =
      ((this.ideaIndexInFormat + 1) / formatIdeas.length * 100) + '%';

    // Update save indicator
    this.updateSaveInd();

    // Animation out
    if (anim) {
      cont.classList.remove('visible');
      cont.classList.add('pres-detonating-out');
      await this.wait(200);
      const r = cont.getBoundingClientRect();
      this.particles.emit(r.left + r.width / 2, r.top + r.height / 2);
      this.sounds.detonate();
      this.overlay.classList.add('pres-shake');
      setTimeout(() => this.overlay.classList.remove('pres-shake'), 150);
    }

    // Render new idea
    cont.innerHTML = this.render(idea);
    cont.classList.remove('pres-detonating-out');
    if (anim) cont.classList.add('pres-detonating-in');
    await this.wait(50);
    cont.classList.add('visible');
    this.animateEls();
    this.overlay.querySelector('.pres-content').scrollTop = 0;
  }

  render(idea) {
    const fmt = (idea.format || 'print').toLowerCase();
    const catColor = idea.categoryColor || '#000000';

    // Determine layout class based on format
    let layoutClass = 'pres-layout-default';
    if (fmt === 'print' || fmt === 'digital') {
      layoutClass = 'pres-layout-landscape'; // Horizontal image
    } else if (fmt === 'social') {
      layoutClass = 'pres-layout-portrait'; // Vertical image
    } else if (fmt === 'experiential') {
      layoutClass = 'pres-layout-square';
    }

    // Get image URL
    let imageUrl = idea.imageUrl;
    const hasRealImage = !!imageUrl;
    if (!imageUrl && VISUAL_FORMATS.includes(fmt)) {
      imageUrl = PRES_DUMMY_IMAGES[presDummyIndex % PRES_DUMMY_IMAGES.length];
      presDummyIndex++;
    }

    const hasImage = VISUAL_FORMATS.includes(fmt);

    let html = `<div class="pres-idea-inner ${layoutClass}" style="--cat-color: ${catColor}">`;

    // TITLE BAR
    html += `<div class="pres-title-bar">
      <div class="pres-title-format-badge">${FORMAT_NAMES[fmt] || fmt.toUpperCase()}</div>
      <div class="pres-title-content">
        <div class="pres-title"><span class="pres-title-label">IDEA:</span> ${this.esc(idea.title)}</div>
        ${idea.idea ? `<div class="pres-title-insight">${this.esc(idea.idea)}</div>` : ''}
      </div>
      <div class="pres-title-trigger">
        <span class="pres-trigger-badge" style="background: ${catColor}">${idea.triggerCode || ''}</span>
      </div>
    </div>`;

    // MAIN CONTENT AREA
    html += `<div class="pres-body">`;

    // Image section (for visual formats)
    if (hasImage) {
      html += `<div class="pres-image-section">
        <div class="pres-image-frame">
          <img src="${imageUrl}" alt="Visual" class="pres-main-image" />
          ${!hasRealImage ? '<div class="pres-image-placeholder-label">PLACEHOLDER</div>' : ''}
        </div>
      </div>`;
    }

    // Content section
    html += `<div class="pres-content-section">`;

    // Headline card (inverted)
    if (idea.headline) {
      html += `<div class="pres-card pres-headline-card">
        <span class="pres-card-label">${idea.headlineLabel || 'HEADLINE'}</span>
        <div class="pres-card-content">${this.esc(idea.headline)}</div>
      </div>`;
    }

    // Subline
    if (idea.subline) {
      html += `<div class="pres-card pres-subline-card">
        <span class="pres-card-label" style="color: ${catColor}">SUBLINE</span>
        <div class="pres-card-content">${this.esc(idea.subline)}</div>
      </div>`;
    }

    // Format-specific content
    html += this.renderContent(idea, fmt, catColor);

    html += `</div>`; // Close content section
    html += `</div>`; // Close body

    // WHY IT WORKS section
    if (idea.whyItWorks) {
      html += `<div class="pres-why-section">
        <div class="pres-why-card" style="--cat-color: ${catColor}">
          <div class="pres-why-header">
            <span class="pres-why-badge" style="background: ${catColor}">${idea.triggerCode || ''}</span>
            <span class="pres-why-trigger-name">${this.esc(idea.triggerName || '')}</span>
          </div>
          <div class="pres-why-label">■ WHY IT WORKS</div>
          <div class="pres-why-content">${this.esc(idea.whyItWorks)}</div>
        </div>
      </div>`;
    }

    html += `</div>`; // Close inner

    return html;
  }

  renderContent(idea, fmt, catColor = '#000000') {
    const text = idea.text || idea.visual || '';
    const sections = this.parseSections(text);
    const labelStyle = `style="color: ${catColor}"`;

    if (fmt === 'print') {
      return `<div class="pres-card"><span class="pres-card-label" ${labelStyle}>VISUAL</span><div class="pres-card-content">${this.esc(text)}</div></div>`;
    }

    if (fmt === 'video') {
      const dur = sections.DURATION || '';
      const shots = sections.SHOTS || '';
      const audio = sections.AUDIO || '';

      let shotsHtml = '';
      if (shots) {
        const items = shots.split(/\n/).filter(s => s.trim());
        shotsHtml = '<div class="pres-timeline">' + items.map(s => {
          const tm = s.match(/\(([^)]+)\)/);
          const desc = s.replace(/\([^)]+\)/, '').trim();
          return `<div class="pres-timeline-item">${tm ? `<div class="pres-timeline-time">${tm[1]}</div>` : ''}<div>${this.esc(desc)}</div></div>`;
        }).join('') + '</div>';
      }

      let html = `<div class="pres-card"><span class="pres-card-label" ${labelStyle}>EXECUTION ${dur ? '• ' + dur : ''}</span><div class="pres-card-content">${shotsHtml || this.esc(shots || text)}</div></div>`;
      if (audio) html += `<div class="pres-card"><span class="pres-card-label" ${labelStyle}>AUDIO</span><div class="pres-card-content">${this.esc(audio)}</div></div>`;
      return html;
    }

    if (fmt === 'social') {
      const hook = sections['HOOK'] || sections['HOOK (First Frame)'] || '';
      const seq = sections.SEQUENCE || '';
      const snd = sections.SOUND || '';

      let html = '';
      if (hook) html += `<div class="pres-card"><span class="pres-card-label" ${labelStyle}>🎯 HOOK</span><div class="pres-card-content">${this.esc(hook)}</div></div>`;
      if (seq) html += `<div class="pres-card"><span class="pres-card-label" ${labelStyle}>SEQUENCE</span><div class="pres-card-content">${this.esc(seq)}</div></div>`;
      if (snd) html += `<div class="pres-card"><span class="pres-card-label" ${labelStyle}>🎵 SOUND</span><div class="pres-card-content">${this.esc(snd)}</div></div>`;
      return html || `<div class="pres-card"><span class="pres-card-label" ${labelStyle}>EXECUTION</span><div class="pres-card-content">${this.esc(text)}</div></div>`;
    }

    if (fmt === 'experiential') {
      const scale = sections.SCALE || '';
      const setup = sections.SETUP || '';
      const inter = sections.INTERACTION || '';
      const reveal = sections['THE REVEAL'] || sections.REVEAL || '';
      const capture = sections.CAPTURE || '';
      const take = sections.TAKEAWAY || '';

      let html = `<div class="pres-journey">${['SETUP', 'INTERACT', 'REVEAL', 'CAPTURE'].map((l, i) =>
        `<div class="pres-journey-step"><div class="pres-journey-num">${i + 1}</div><div class="pres-journey-label">${l}</div></div>`
      ).join('')}</div>`;

      html += `<div class="pres-card"><span class="pres-card-label" ${labelStyle}>ACTIVATION ${scale ? '• ' + scale : ''}</span><div class="pres-card-content">`;
      if (setup) html += `<div class="pres-section"><div class="pres-section-label">SETUP</div><div class="pres-section-content">${this.esc(setup)}</div></div>`;
      if (inter) html += `<div class="pres-section"><div class="pres-section-label">INTERACTION</div><div class="pres-section-content">${this.esc(inter)}</div></div>`;
      if (reveal) html += `<div class="pres-section"><div class="pres-section-label">★ THE REVEAL</div><div class="pres-section-content">${this.esc(reveal)}</div></div>`;
      if (capture) html += `<div class="pres-section"><div class="pres-section-label">CAPTURE</div><div class="pres-section-content">${this.esc(capture)}</div></div>`;
      if (take) html += `<div class="pres-section"><div class="pres-section-label">TAKEAWAY</div><div class="pres-section-content">${this.esc(take)}</div></div>`;
      return html + '</div></div>';
    }

    if (fmt === 'digital') {
      const sizes = sections.SIZES || '';
      const anim = sections.ANIMATION || '';
      const inter = sections.INTERACTION || '';

      let html = `<div class="pres-card"><span class="pres-card-label" ${labelStyle}>ANIMATION ${sizes ? '• ' + sizes : ''}</span><div class="pres-card-content">${this.esc(anim || text)}</div></div>`;
      if (inter) html += `<div class="pres-card"><span class="pres-card-label" ${labelStyle}>INTERACTION</span><div class="pres-card-content">${this.esc(inter)}</div></div>`;
      return html;
    }

    if (fmt === 'campaign') {
      const big = sections['THE BIG IDEA'] || '';
      const hero = sections['HERO EXECUTION'] || '';
      const ext = sections.EXTENSIONS || '';
      const part = sections.PARTICIPATION || '';

      let html = '';
      if (big) html += `<div class="pres-card"><span class="pres-card-label" ${labelStyle}>THE BIG IDEA</span><div class="pres-card-content">${this.esc(big)}</div></div>`;
      if (hero) html += `<div class="pres-card"><span class="pres-card-label" ${labelStyle}>HERO EXECUTION</span><div class="pres-card-content">${this.esc(hero)}</div></div>`;
      if (ext) {
        const exts = ext.split(/\n/).filter(s => s.trim() && s.includes(':'));
        if (exts.length) {
          html += `<div class="pres-card"><span class="pres-card-label" ${labelStyle}>EXTENSIONS</span><div class="pres-card-content"><div class="pres-extensions">`;
          exts.forEach(e => {
            const [ch, ...rest] = e.split(':');
            html += `<div class="pres-ext-channel">${this.esc(ch.trim())}</div><div class="pres-ext-desc">${this.esc(rest.join(':').trim())}</div>`;
          });
          html += '</div></div></div>';
        }
      }
      if (part) html += `<div class="pres-card"><span class="pres-card-label" ${labelStyle}>PARTICIPATION</span><div class="pres-card-content">${this.esc(part)}</div></div>`;
      return html || `<div class="pres-card"><span class="pres-card-label" ${labelStyle}>PLATFORM</span><div class="pres-card-content">${this.esc(text)}</div></div>`;
    }

    return `<div class="pres-card"><span class="pres-card-label" ${labelStyle}>CONTENT</span><div class="pres-card-content">${this.esc(text)}</div></div>`;
  }

  parseSections(text) {
    const result = {};
    if (!text) return result;

    const lines = text.split('\n');
    let cur = null;
    let content = [];
    const labels = [
      'DURATION', 'SHOTS', 'AUDIO', 'FORMAT', 'HOOK', 'HOOK (First Frame)',
      'SEQUENCE', 'SOUND', 'SCALE', 'SETUP', 'INTERACTION', 'THE REVEAL',
      'REVEAL', 'CAPTURE', 'TAKEAWAY', 'SIZES', 'ANIMATION', 'THE BIG IDEA',
      'HERO EXECUTION', 'EXTENSIONS', 'PARTICIPATION'
    ];

    for (const line of lines) {
      const t = line.trim();
      let found = null;
      for (const l of labels) {
        if (t.startsWith(l + ':') || t === l + ':') {
          found = l;
          const after = t.substring(l.length + 1).trim();
          content = after ? [after] : [];
          break;
        }
      }
      if (found) {
        if (cur && content.length) result[cur] = content.join('\n').trim();
        cur = found;
        content = content.length ? content : [];
      } else if (cur && t) {
        content.push(t);
      }
    }
    if (cur && content.length) result[cur] = content.join('\n').trim();
    return result;
  }

  async animateEls() {
    const c = this.overlay.querySelector('.pres-idea');
    const title = c.querySelector('.pres-title-bar');
    if (title) { await this.wait(80); title.classList.add('animate-in'); }
    const img = c.querySelector('.pres-image-section');
    if (img) { await this.wait(80); img.classList.add('animate-in'); }
    const cards = c.querySelectorAll('.pres-card');
    for (const card of cards) { await this.wait(60); card.classList.add('animate-in'); }
    const why = c.querySelector('.pres-why-section');
    if (why) { await this.wait(80); why.classList.add('animate-in'); }
  }

  // ============================================================================
  // ACTIONS
  // ============================================================================

  toggleSave() {
    const idea = this.ideas[this.idx];
    const id = idea.id || this.idx;

    if (this.saved.has(id)) {
      this.saved.delete(id);
      this.sounds.unsave();
      this.notify('Removed');
    } else {
      this.saved.add(id);
      this.sounds.save();
      this.notify('Saved ★');
    }

    this.updateSaveInd();
    if (this.onSave) this.onSave(idea, this.saved.has(id));
  }

  updateSaveInd() {
    const ind = this.overlay.querySelector('.pres-save-ind');
    const id = this.ideas[this.idx]?.id || this.idx;
    ind.classList.toggle('saved', this.saved.has(id));
  }

  async copy() {
    const idea = this.ideas[this.idx];
    const txt = `${idea.title}\n\n${idea.idea ? 'THE IDEA: ' + idea.idea + '\n\n' : ''}${idea.headline ? idea.headline + '\n\n' : ''}${idea.subline ? idea.subline + '\n\n' : ''}${idea.text || ''}\n\n${idea.whyItWorks ? 'WHY IT WORKS: ' + idea.whyItWorks : ''}`;

    try {
      await navigator.clipboard.writeText(txt);
      this.sounds.copy();
      this.notify('Copied!');
    } catch {
      this.notify('Failed');
    }
  }

  toggleSound() {
    const on = this.sounds.toggle();
    this.overlay.querySelector('.pres-sound-label').textContent = on ? 'ON' : 'OFF';
    this.overlay.querySelector('.pres-action-sound').classList.toggle('muted', !on);
    this.notify(on ? 'Sound ON' : 'Sound OFF');
  }

  showHelp() {
    this.overlay.querySelector('.pres-help').classList.remove('hidden');
  }

  hideHelp() {
    this.overlay.querySelector('.pres-help').classList.add('hidden');
  }

  showSummary() {
    this.overlay.querySelector('.pres-viewed').textContent = this.ideas.length;
    this.overlay.querySelector('.pres-savedct').textContent = this.saved.size;
    this.overlay.querySelector('.pres-summary-stars').textContent = '★'.repeat(Math.min(this.saved.size, 5));
    this.overlay.querySelector('.pres-summary').classList.remove('hidden');
  }

  notify(msg) {
    const n = this.overlay.querySelector('.pres-notification');
    n.textContent = msg;
    n.classList.add('visible');
    setTimeout(() => n.classList.remove('visible'), 1500);
  }

  wait(ms) {
    return new Promise(r => setTimeout(r, ms));
  }

  esc(t) {
    if (!t) return '';
    const d = document.createElement('div');
    d.textContent = t;
    return d.innerHTML;
  }
}

// Global export
window.PresentationMode = PresentationMode;
